import { deleteScope, getScopes, IScope } from "@/api";

import { formatDate, handleRowSelection } from "@/utils";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import {
    GridActionsCellItem,
    GridColDef,
    GridRowSelectionModel,
    GridSortModel,
} from "@mui/x-data-grid-pro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";

import { AdminPageHeader, SearchField } from "@/components/admin";
import { Button } from "@/components/shared/buttons/Button";
import { UserChip } from "@/components/shared/chips/UserChip";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { DestructiveDialogProps } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog.types";
import { ScopeStatusCell } from "@/components/shared/table/cells/ScopeStatusCell";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { Link, Typography } from "@mui/material";
import { FileSearch2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { formatCompactDuration } from "../job-run/utils";

export const ScopesTable = () => {
    const handleClick = async () => {
        await dialogs.open(DestructiveDialog, dialogProps);
    };
    const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedScopeIds(handleRowSelection(rowSelectionModel, rows));
    };

    const dialogs = useDialogs();
    const queryClient = useQueryClient();

    const dialogProps: DestructiveDialogProps = {
        itemIds: selectedScopeIds,
        subject: "scopes",
        mutationFn: deleteScope,
        onSucceeded: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-scopes"],
            });
        },
    };

    const handleDelete = useCallback(
        async (id: number) => {
            const dialogProps: DestructiveDialogProps = {
                itemId: id,
                subject: "scope",
                mutationFn: deleteScope,
                onSucceeded: () => {
                    queryClient.invalidateQueries({
                        queryKey: ["admin-scopes"],
                    });
                },
            };

            await dialogs.open(DestructiveDialog, dialogProps);
        },
        [dialogs, queryClient]
    );

    const [rows, setRows] = useState<IScope[]>([]);
    const columns: GridColDef<IScope>[] = useMemo(
        () => [
            {
                field: "document_title",
                headerName: "Scope",
                display: "flex",
                maxWidth: 300,
                renderCell: (params) => (
                    <Link
                        component={RouterLink}
                        to={`/admin/scopes/${params.row.id}/job-run`}
                        underline="none"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                    >
                        <FileSearch2 size={16} style={{ color: "#233876" }} />
                        <Typography
                            variant="body2"
                            sx={{ ml: 1 }}
                            fontWeight={600}
                            color="primary"
                        >
                            {params.row.document_title}
                        </Typography>
                    </Link>
                ),
            },
            {
                field: "clause_checklist",
                headerName: "Clause Checklist",
                display: "flex",
                valueGetter: (_, row) => {
                    return row.clause_checklist.name;
                },
            },
            {
                field: "status",
                headerName: "Status",
                display: "flex",
                type: "singleSelect",
                valueOptions: [
                    {
                        label: "Waiting for Document",
                        value: "pending",
                    },
                    {
                        label: "Processing",
                        value: "processing",
                    },
                    {
                        label: "Complete",
                        value: "complete",
                    },
                    {
                        label: "Failed",
                        value: "failed",
                    },
                ],
                renderCell: (params) => {
                    return <ScopeStatusCell scope={params.row} />;
                },
            },
            {
                field: "completion_time",
                headerName: "Completion Time",
                display: "flex",
                valueGetter: (_, row) => {
                    const startedAt = row.job_started_at ?? row.created_at;
                    const completedAt =
                        row.job_completed_at ?? row.processing_completed_at;
                    if (!completedAt) return "-";

                    return formatCompactDuration(
                        new Date(startedAt),
                        new Date(completedAt)
                    );
                },
            },
            {
                field: "page_count",
                headerName: "Page Count",
                display: "flex",
                valueGetter: (_, row) => {
                    return row.document.page_count || "-";
                },
            },

            {
                field: "user",
                headerName: "User",
                display: "flex",
                renderCell: (params) => {
                    return <UserChip user={params.row.user} />;
                },
            },
            {
                field: "organization",
                headerName: "User Organization",
                display: "flex",
                valueGetter: (_, row) => {
                    return row.organization.name;
                },
            },
            {
                field: "created_at",
                headerName: "Upload Date",
                display: "flex",
                type: "date",
                valueFormatter: (value) => {
                    return formatDate(value);
                },
            },
            {
                field: "actions",
                headerName: "Actions",
                type: "actions",
                getActions: ({ row }) => {
                    return [
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Delete Scope">
                                    <DeleteIcon />
                                </Tooltip>
                            }
                            label="Delete"
                            onClick={() => handleDelete(row.id)}
                        />,
                    ];
                },
            },
        ],
        [handleDelete]
    );

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const autoSizeOptions = {
        columns: [
            "document_title",
            "clause_checklist",
            "status",
            "completion_time",
            "page_count",
            "user",
            "organization",
            "created_at",
        ],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    // Fetch collections from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-scopes",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getScopes(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || null
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Scopes"
                subtitle="Track and manage document analysis runs"
            >
                {selectedScopeIds.length > 0 && (
                    <Button
                        variant="contained"
                        color="tertiary"
                        disableElevation
                        sx={{ borderRadius: 22 }}
                        onClick={handleClick}
                    >
                        Delete Selected
                    </Button>
                )}
            </AdminPageHeader>
            <div className="mt-6 mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search scopes..."
                />
            </div>
            <div>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableColumnResize
                    disableColumnFilter
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode={"server"}
                    sortingMode={"server"}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </>
    );
};
